/**
 * reportCards.js — the Reports hub's card registry (PRD_DtrReport C2).
 *
 * Each card carries its own gate, label and route in one object. `requires` is
 * a string[] of permissions that must ALL be held (evaluated with
 * hasAllPermissions) — deliberately NOT dashboardCards.js's contract, whose
 * `requires` is a single string checked with hasPermission. Only the key NAME
 * is borrowed (C2); no code is shared.
 *
 * Adding a card is this object PLUS permissions.js REPORTS_HUB_ANY (the
 * Reports nav entry and the hub route), which must admit the new card's
 * holders — it cannot be derived from here without an import cycle, since
 * this file imports PERMISSIONS.
 */
import { PERMISSIONS } from './permissions';

/**
 * The DTR route gate (C3): the report reads the DTR document (time:view) AND
 * the employee document for the header's name/department (emp:view), so the
 * route requires the intersection. App.js imports THIS array for the DTR and
 * Attendance Summary routes, so the config and the route guard cannot drift.
 */
export const DTR_REPORT_REQUIRES = [
  PERMISSIONS.TIME_TRACKING_VIEW,
  PERMISSIONS.EMPLOYEES_VIEW,
];

/**
 * The Contribution Basis gate (PRD_ContributionBasisReport C6): the report
 * reads only the payroll index, run docs and their saved employee lines — all
 * readable with payroll:view — and no employee document, so payroll:view alone.
 */
export const CONTRIBUTION_BASIS_REPORT_REQUIRES = [PERMISSIONS.PAYROLL_VIEW];

/**
 * The Loan Remittance gate (PRD_LoanRemittanceReport C8): the report reads the
 * loans (loan:view) and the employee documents for names (emp:view). Member
 * numbers need emp:pii:view too, but only for an optional column the page
 * hides without it (C3), so that key is not part of the gate.
 */
export const LOAN_REMITTANCE_REPORT_REQUIRES = [PERMISSIONS.LOANS_VIEW, PERMISSIONS.EMPLOYEES_VIEW];

/**
 * The Contribution Schedule gate (PRD_ContributionSchedule C12): payroll:view,
 * on the card, the route AND the callable. The report shows daily rates, which
 * payroll:view already sees on every saved run line.
 */
export const CONTRIBUTION_SCHEDULE_REPORT_REQUIRES = [PERMISSIONS.PAYROLL_VIEW];

export const REPORT_CARDS = [
  {
    id: 'dtr',
    label: 'Daily Time Record (DTR)',
    description: 'Printable attendance record for any range up to a month — one employee or the whole roster.',
    icon: 'file-text',
    path: 'reports/dtr',
    requires: DTR_REPORT_REQUIRES,
  },
  {
    id: 'attendance-summary',
    label: 'Attendance Summary',
    description: 'Totals per employee — days, hours, overtime, night differential and breaks — for any range in the last two months.',
    // Not file-text: two cards with one icon are indistinguishable on the hub
    // (PRD_AttendanceSummaryReport C1).
    icon: 'bar-chart-3',
    path: 'reports/attendance-summary',
    // The SAME array, not a copy — it reads the same two things the DTR does,
    // and the Reports nav gate in permissions.js already admits its holders (C2).
    requires: DTR_REPORT_REQUIRES,
  },
  {
    id: 'contribution-basis',
    label: 'Contribution Basis',
    description: "One month's SSS or PhilHealth per employee — how the contribution was arrived at, what payroll deducted, and the difference.",
    icon: 'shield-check',
    path: 'reports/contribution-basis',
    requires: CONTRIBUTION_BASIS_REPORT_REQUIRES,
  },
  {
    id: 'loan-remittance',
    label: 'Loan Remittance',
    description: 'One loan, one month — who paid, how much, and what is left to deduct. For SSS, Pag-IBIG or company loan remittances.',
    icon: 'landmark',
    path: 'reports/loan-remittance',
    requires: LOAN_REMITTANCE_REPORT_REQUIRES,
  },
  {
    id: 'contribution-schedule',
    label: 'Contribution Schedule',
    description: "One month's SSS, PhilHealth and Pag-IBIG per employee — employer and employee shares, from that month's attendance.",
    icon: 'calendar',
    path: 'reports/contribution-schedule',
    requires: CONTRIBUTION_SCHEDULE_REPORT_REQUIRES,
  },
];
